import call from "./call";
import { GetWorkspaceResponse, Workspace } from "../Models/workspace";

export const createWorkspace = async (
  name: string,
  description: string,
  userEvaluationLimit: number,
  userCompanyLimit: number,
  userStorageLimit: number,
  userLibraryLimit: number,
  namespaces: string[],
  iconImage: File | null,
  workspaceAdminId: number | null,
  features: number[] = [],
  certifications: number[] = [],
  model_ids: number[],
  experts: string[],
  primary_color: string | null,
  secondary_color: string | null,
  accent_color: string | null,
  terminology: number,
  alan_credentials_id: number | null,
  language: number,
  show_locked_features: boolean,
  agent_ids: number[] = [],
): Promise<Record<string, unknown>> => {
  const formData = new FormData();

  formData.append("name", name);
  formData.append("description", description);
  formData.append("user_evaluation_limit", userEvaluationLimit.toString());
  formData.append("user_company_limit", userCompanyLimit.toString());
  formData.append("user_storage_limit", userStorageLimit.toString());
  formData.append("user_library_limit", userLibraryLimit.toString());
  formData.append("namespaces", JSON.stringify(namespaces));
  formData.append("features", JSON.stringify(features));
  formData.append("certifications", JSON.stringify(certifications));
  formData.append("model_ids", JSON.stringify(model_ids));
  formData.append("terminology", terminology.toString());
  formData.append("experts", JSON.stringify(experts));
  formData.append("primary_color", primary_color || "");
  formData.append("secondary_color", secondary_color || "");
  formData.append("accent_color", accent_color || "");
  formData.append("agent_ids", JSON.stringify(agent_ids));

  if (workspaceAdminId !== null) {
    formData.append("workspace_admin_id", workspaceAdminId.toString());
  }

  if (alan_credentials_id !== null) {
    formData.append("alan_credentials_id", alan_credentials_id.toString());
  }

  if (iconImage) {
    formData.append("icon_image", iconImage);
  }

  formData.append("language", language.toString());
  formData.append("show_locked_features", show_locked_features.toString());

  const { data } = await call.post("/v1/workspace/", formData, {
    snack: {
      success: "workspace.createdSuccess",
      error: "workspace.createError",
    },
  });

  return data;
};

export const getAllWorkspaces = async (
  page: number,
  pageSize: number,
): Promise<{ workspaces: Workspace[]; total: number }> => {
  const { data } = await call.get<{
    workspaces: Workspace[];
    total: number;
  }>("/v1/workspace/", {
    params: { page, page_size: pageSize },
    snack: {
      error: "workspace.fetchError",
    },
  });
  return data;
};

export const getWorkspace = async (
  workspaceId: string,
): Promise<GetWorkspaceResponse> => {
  const { data } = await call.get<GetWorkspaceResponse>(
    `/v1/workspace/${workspaceId}/`,
    {
      snack: {
        error: "workspace.loadDetailsError",
      },
    },
  );
  return data;
};

export const updateWorkspace = async (
  workspaceId: number,
  name: string,
  description: string,
  userEvaluationLimit: number,
  userCompanyLimit: number,
  userStorageLimit: number,
  userLibraryLimit: number,
  namespaces: string[],
  iconImage: File | null,
  workspaceAdminId: number | null,
  features: number[] = [],
  certifications: number[] = [],
  model_ids: number[],
  experts: string[],
  primary_color: string | null,
  secondary_color: string | null,
  accent_color: string | null,
  terminology: number,
  alan_credentials_id: number | null,
  language: number,
  show_locked_features: boolean,
  agent_ids: number[] = [],
): Promise<GetWorkspaceResponse> => {
  const formData = new FormData();

  if (name) formData.append("name", name);
  if (description) formData.append("description", description);
  if (userEvaluationLimit)
    formData.append("user_evaluation_limit", userEvaluationLimit.toString());
  if (userCompanyLimit)
    formData.append("user_company_limit", userCompanyLimit.toString());
  if (namespaces && namespaces.length > 0)
    if (userStorageLimit !== undefined)
      formData.append("user_storage_limit", userStorageLimit.toString());
  if (userLibraryLimit !== undefined)
    formData.append("user_library_limit", userLibraryLimit.toString());
  formData.append("namespaces", JSON.stringify(namespaces));
  if (features && features.length > 0)
    formData.append("features", JSON.stringify(features.map(Number)));
  if (certifications && certifications.length > 0)
    formData.append(
      "certifications",
      JSON.stringify(certifications.map(Number)),
    );
  if (workspaceAdminId !== null)
    formData.append("workspace_admin_id", workspaceAdminId.toString());
  formData.append("model_ids", JSON.stringify(model_ids));
  formData.append("terminology", terminology.toString());

  if (language !== undefined) {
    formData.append("language", language.toString());
  }

  if (iconImage) {
    formData.append("icon_image", iconImage);
  }
  if (experts) {
    formData.append("experts", JSON.stringify(experts));
  }
  if (primary_color) formData.append("primary_color", primary_color);
  if (secondary_color) formData.append("secondary_color", secondary_color);
  if (accent_color) formData.append("accent_color", accent_color);

  if (alan_credentials_id !== null) {
    formData.append("alan_credentials_id", alan_credentials_id.toString());
  }

  formData.append("show_locked_features", show_locked_features.toString());
  formData.append("agent_ids", JSON.stringify(agent_ids));

  const { data } = await call.put(`/v1/workspace/${workspaceId}/`, formData, {
    snack: {
      success: "workspace.updatedSuccess",
      error: "workspace.updateError",
    },
  });
  return data;
};

export const deleteWorkspace = async (workspaceId: number): Promise<void> => {
  await call.delete(`/v1/workspace/${workspaceId}/`, {
    snack: {
      success: "workspace.deletedSuccess",
      error: "workspace.deleteError",
    },
  });
};

export const getCurrentWorkspace = async (): Promise<GetWorkspaceResponse> => {
  const { data } = await call.get<GetWorkspaceResponse>(
    "/v1/workspace/current/",
  );
  return data;
};

export const getAllWorkspaceTerminologies = async (): Promise<{
  terminologies: { name: string; value: number }[];
}> => {
  const { data } = await call.get<{
    terminologies: { name: string; value: number }[];
  }>("/v1/workspace/terminology/", {
    snack: {
      error: "workspace.fetchTerminologiesError",
    },
  });
  return data;
};
